import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface Project {
  id: number;
  name: string;
}

interface CompanyStats {
  name: string;
  candidate_count: number;
  country: string;
  industry: string;
}

interface IndustryStats {
  industry: string;
  candidate_count: number;
  company_count: number;
}

interface GeographicStats {
  country: string;
  company_count: number;
  candidate_count: number;
}

interface OpenToWorkCandidate {
  id: number;
  name: string;
  current_position: string;
  current_company: string;
  location_country: string;
  years_at_current_company: number;
  email: string;
  phone: string;
  li_profile_url: string;
}

interface IntelligenceData {
  topCompanies: CompanyStats[];
  industryBreakdown: IndustryStats[];
  geographicDistribution: GeographicStats[];
  openToWork: OpenToWorkCandidate[];
  totalStats: {
    totalProjects: number;
    totalCandidates: number;
    totalCompanies: number;
    totalCountries: number;
  };
}

const MarketIntelligence: React.FC = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [intelligenceData, setIntelligenceData] = useState<IntelligenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllOpenToWork, setShowAllOpenToWork] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [token]);

  useEffect(() => {
    if (selectedProject) {
      fetchIntelligenceData();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchIntelligenceData = async () => {
    try {
      setLoading(true);
      const endpoint = selectedProject === 'all'
        ? '/api/market-intelligence/all'
        : `/api/market-intelligence/project/${selectedProject}`;

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setIntelligenceData(data);
      }
    } catch (error) {
      console.error('Error fetching intelligence data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !intelligenceData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#2145b9] border-t-transparent"></div>
          <p className="mt-4 text-sm text-gray-600 font-medium">Loading market intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">Market Monitoring</h1>
        <p className="text-gray-600">Analyse territory data and identify market opportunities</p>
      </div>

      {/* Project Selector */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Viewing Intelligence For:
        </label>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#2145b9] focus:border-transparent transition-all"
        >
          <option value="all">All Projects Combined</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {intelligenceData && (
        <>
          {/* Placeholder: Hiring & Losing Trends */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-dashed border-amber-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Hiring & Losing Trends</h3>
                <p className="text-gray-700 mb-4">Track which companies are actively hiring and which are losing talent in the last 3 months.</p>
                <div className="bg-white/60 rounded-xl p-4 border border-amber-200">
                  <p className="text-sm text-gray-600 mb-2">🔒 <strong>Coming Soon:</strong> Candidate Monitoring</p>
                  <p className="text-sm text-gray-600">Enable real-time tracking to unlock insights into hiring trends, candidate movements, and talent flow patterns across your territory.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Companies by Current Candidate Count */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2145b9] to-[#2f7cf2] flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Top Companies by Current Talent Pool
              </h3>
            </div>

            {intelligenceData.topCompanies.length > 0 ? (
              <div className="space-y-3">
                {intelligenceData.topCompanies.slice(0, 10).map((company, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-4 px-4 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#2145b9] to-[#2f7cf2] flex items-center justify-center">
                        <span className="text-white font-bold text-sm">#{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{company.name}</p>
                        <p className="text-sm text-gray-600">
                          {company.industry || 'Industry unknown'} • {company.country || 'Location unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-2xl font-bold text-[#2145b9]">{company.candidate_count}</p>
                      <p className="text-xs text-gray-500">candidates</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No company data available</p>
              </div>
            )}
          </div>

          {/* Industry Trends */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Industry Distribution
              </h3>
            </div>

            {intelligenceData.industryBreakdown.length > 0 ? (
              <div className="space-y-4">
                {intelligenceData.industryBreakdown.map((industry, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{industry.industry}</p>
                        <p className="text-sm text-gray-600">
                          {industry.candidate_count} candidates across {industry.company_count} companies
                        </p>
                      </div>
                      <span className="text-lg font-bold text-[#2145b9]">{industry.candidate_count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#2145b9] to-[#2f7cf2] rounded-full"
                        style={{
                          width: `${Math.min((industry.candidate_count / Math.max(...intelligenceData.industryBreakdown.map(i => i.candidate_count))) * 100, 100)}%`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No industry data available</p>
              </div>
            )}
          </div>

          {/* Geographic Distribution */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Geographic Distribution
              </h3>
            </div>

            {intelligenceData.geographicDistribution.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {intelligenceData.geographicDistribution.map((geo, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl border border-gray-100 hover:border-[#2145b9]/30 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <p className="font-semibold text-gray-900">{geo.country}</p>
                      <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-[#2145b9] text-xs font-semibold rounded-full">
                        {geo.company_count} {geo.company_count === 1 ? 'company' : 'companies'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
                          style={{
                            width: `${Math.min((geo.candidate_count / Math.max(...intelligenceData.geographicDistribution.map(g => g.candidate_count))) * 100, 100)}%`
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-700 flex-shrink-0">
                        {geo.candidate_count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No geographic data available</p>
              </div>
            )}
          </div>

          {/* Open to Work Candidates */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Open to Work Candidates
                </h3>
              </div>
              {intelligenceData.openToWork.length > 5 && (
                <button
                  onClick={() => setShowAllOpenToWork(!showAllOpenToWork)}
                  className="text-sm text-[#2145b9] hover:text-[#2f7cf2] font-medium inline-flex items-center gap-1"
                >
                  {showAllOpenToWork ? 'Show Less' : `Show All (${intelligenceData.openToWork.length})`}
                  <svg className={`w-4 h-4 transition-transform ${showAllOpenToWork ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>

            {intelligenceData.openToWork.length > 0 ? (
              <div className="space-y-3">
                {(showAllOpenToWork ? intelligenceData.openToWork : intelligenceData.openToWork.slice(0, 5)).map((candidate) => (
                  <div
                    key={candidate.id}
                    className="flex items-start justify-between py-4 px-4 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-semibold text-gray-900">{candidate.name}</p>
                        <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          Open to Work
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-1">
                        {candidate.current_position} at {candidate.current_company}
                      </p>
                      <p className="text-sm text-gray-600">
                        {candidate.location_country} • {candidate.years_at_current_company ? `${candidate.years_at_current_company} years at current company` : 'Tenure unknown'}
                      </p>
                      {(candidate.email || candidate.phone) && (
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          {candidate.email && (
                            <a href={`mailto:${candidate.email}`} className="text-[#2145b9] hover:text-[#2f7cf2] flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Email
                            </a>
                          )}
                          {candidate.phone && (
                            <a href={`tel:${candidate.phone}`} className="text-[#2145b9] hover:text-[#2f7cf2] flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              Call
                            </a>
                          )}
                          {candidate.li_profile_url && (
                            <a href={candidate.li_profile_url} target="_blank" rel="noopener noreferrer" className="text-[#2145b9] hover:text-[#2f7cf2] flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                              LinkedIn
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No candidates currently marked as open to work</p>
              </div>
            )}
          </div>

          {/* Candidate Monitoring Promo Card */}
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            {/* Premium gradient background with layered effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0f1629] via-[#1a2744] to-[#2145b9]"></div>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(47,124,242,0.3)_0%,_transparent_60%)]"></div>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(139,92,246,0.2)_0%,_transparent_60%)]"></div>
            {/* Subtle grid pattern overlay */}
            <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '32px 32px'}}></div>
            {/* Floating accent orbs */}
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-gradient-to-br from-blue-400/20 to-purple-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-8 -left-8 w-36 h-36 bg-gradient-to-tr from-cyan-400/15 to-blue-500/10 rounded-full blur-2xl"></div>

            <div className="relative p-10">
              {/* Header section */}
              <div className="flex items-start gap-5 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/25 ring-1 ring-white/10">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase text-blue-300/80 mb-2">Candidate Monitoring</p>
                  <h3 className="text-2xl font-bold text-white leading-tight">Automated Candidate Profile Enrichment</h3>
                </div>
              </div>

              {/* Feature grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/[0.06] backdrop-blur-sm rounded-xl p-5 border border-white/[0.08] hover:bg-white/[0.09] transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400/20 to-emerald-500/10 flex items-center justify-center ring-1 ring-emerald-400/20">
                      <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-white text-sm">Change Detection</h4>
                  </div>
                  <p className="text-sm text-blue-200/70 leading-relaxed">Automatically detect profile changes, promotions, job moves, and backfill opportunities across your candidate pool.</p>
                </div>

                <div className="bg-white/[0.06] backdrop-blur-sm rounded-xl p-5 border border-white/[0.08] hover:bg-white/[0.09] transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-400/20 to-violet-500/10 flex items-center justify-center ring-1 ring-violet-400/20">
                      <svg className="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-white text-sm">Smart Adaptive Monitoring</h4>
                  </div>
                  <p className="text-sm text-blue-200/70 leading-relaxed">Customise the frequency of profile updates based on each candidate's role tenure — monitor high-priority candidates more often.</p>
                </div>

                <div className="bg-white/[0.06] backdrop-blur-sm rounded-xl p-5 border border-white/[0.08] hover:bg-white/[0.09] transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400/20 to-amber-500/10 flex items-center justify-center ring-1 ring-amber-400/20">
                      <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-white text-sm">Instant Alerts</h4>
                  </div>
                  <p className="text-sm text-blue-200/70 leading-relaxed">Receive instant email notifications on key changes as they happen, plus a weekly digest summarising all activity.</p>
                </div>

                <div className="bg-white/[0.06] backdrop-blur-sm rounded-xl p-5 border border-white/[0.08] hover:bg-white/[0.09] transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-400/20 to-cyan-500/10 flex items-center justify-center ring-1 ring-cyan-400/20">
                      <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-white text-sm">Credit-Based Plans</h4>
                  </div>
                  <p className="text-sm text-blue-200/70 leading-relaxed">Flexible credit-based pricing that scales with your needs. Only pay for the monitoring you use.</p>
                </div>
              </div>

              {/* Pricing badge and CTA */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/[0.08] backdrop-blur-sm rounded-xl px-5 py-3 border border-white/[0.1]">
                    <p className="text-xs text-blue-300/70 mb-0.5">Starting from</p>
                    <p className="text-2xl font-bold text-white">£149<span className="text-sm font-normal text-blue-300/70">/mo</span></p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/25 ring-1 ring-white/10">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Coming Soon
                </div>
              </div>
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Intelligence Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-2xl font-bold text-[#2145b9]">{intelligenceData.totalStats.totalCandidates.toLocaleString()}</p>
                <p className="text-sm text-gray-600">Total Candidates</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2145b9]">{intelligenceData.totalStats.totalCompanies.toLocaleString()}</p>
                <p className="text-sm text-gray-600">Unique Companies</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2145b9]">{intelligenceData.industryBreakdown.length}</p>
                <p className="text-sm text-gray-600">Industries Tracked</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2145b9]">{intelligenceData.openToWork.length}</p>
                <p className="text-sm text-gray-600">Open to Work</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketIntelligence;
